import { useEffect, useState } from 'react';

import _debounce from 'lodash/debounce';

import { MenuItem, MenuSection } from '../../../../core/api/menu/menu.types';

type UseMenuSearch = {
  data: MenuItem[];
  isDataEmpty: boolean;
  isSearchValueEmpty: boolean;
};

export const useMenuSearch = (
  menuSections: MenuSection[],
  searchValue: string,
): UseMenuSearch => {
  const [filteredMenuItems, setFilteredMenuItems] = useState<MenuItem[]>([]);
  const [isDataEmpty, setIsDataEmpty] = useState<boolean>(false);
  const isSearchValueEmpty = searchValue.length === 0;

  const filterMenu = (): void => {
    const isValidMenuSearch = !!menuSections.length && !isSearchValueEmpty;
    if (isValidMenuSearch) {
      const newFilteredMenuItems: MenuItem[] = [];

      // Expensive menu search - for loop is the most performant
      for (let i = 0; i < menuSections.length; i += 1) {
        const section = menuSections[i];

        for (let j = 0; j < section.MenuItems.length; j += 1) {
          const item = section.MenuItems[j];
          const name = item.Name ? item.Name.toLowerCase() : '';
          const search = searchValue.toLowerCase();

          if (name.includes(search)) {
            newFilteredMenuItems.push(item);
          }
        }
      }

      const isArrayEmpty = newFilteredMenuItems.length === 0;

      setIsDataEmpty(isArrayEmpty);
      setFilteredMenuItems(newFilteredMenuItems);
    } else {
      setIsDataEmpty(false);
      setFilteredMenuItems([]);
    }
  };

  const debouncedFilterMenu = _debounce(filterMenu, 200);

  useEffect(() => {
    const isFirstSearchCharacter = searchValue.length === 1;
    // If the user has only typed one character, don't debounce the search
    if (isFirstSearchCharacter) {
      filterMenu();
    } else {
      debouncedFilterMenu();
    }

    return () => {
      debouncedFilterMenu.cancel(); // Cancel any pending debounce calls on unmount
    };
  }, [searchValue]);

  return {
    data: filteredMenuItems,
    isDataEmpty,
    isSearchValueEmpty,
  };
};
